import React, { ChangeEvent } from 'react';

import { HomeOutlined, UserOutlined } from '@ant-design/icons';
import { Card, Col, Flex, Row, Select } from 'antd';

import { AddressSummaryCard } from './AddressSummaryCard';
import { DuplicateShipmentAddressForm } from './DuplicateShipmentAddressForm';
import { DuplicateShipmentCarrierForm } from './DuplicateShipmentCarrierForm';
import { EditableCard } from './EditableCard';
import { ParcelDetailsSummary } from './ParcelDetailsSummary';
import TextInput from '@/components/forms/TextInput';
import { ICreateShipmentAddress } from '@/models/address';

interface ICarrierOption {
  carrierCode: string;
  label: string;
}

interface IServiceTypeOption {
  id: number;
  name: string;
  value: string;
}

interface ISnapshotHandlers {
  onEditStart: () => void;
  onCancel: () => void;
}

interface IDuplicateShipmentOutboundSectionProps {
  values: {
    carrier: string;
    reference: string;
    carrierTrackingCode: string;
    weightInGrams: string;
    serviceType: string;
    senderEmail: string;
    recipientEmail: string;
  };
  setFieldValue: (field: string, value: unknown) => void;
  carrierOptions: ICarrierOption[];
  serviceTypeEnabled: boolean;
  serviceTypeOptions: IServiceTypeOption[];
  outboundLabelUrl: string | null;
  file: File | null;
  setFile: (f: File | null) => void;
  parcelHandlers: ISnapshotHandlers;
  senderHandlers: ISnapshotHandlers;
  recipientHandlers: ISnapshotHandlers;
  handleEditingChange: (editing: boolean) => void;
  hasErrorsInFields: (fields: string[]) => boolean;
  getAddressFields: (prefix: string) => string[];
  getAddressFormProps: (prefix: string) => {
    prefix: string;
    values: Record<string, string>;
    errors: Record<string, string | undefined>;
    touched: Record<string, boolean | undefined>;
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    setFieldValue: (field: string, value: string) => void;
    showPhone: boolean;
  };
  getAddressSummary: (prefix: string) => ICreateShipmentAddress;
  hasAddressError: (prefix: string) => boolean;
}

export const DuplicateShipmentOutboundSection = ({
  values,
  setFieldValue,
  carrierOptions,
  serviceTypeEnabled,
  serviceTypeOptions,
  outboundLabelUrl,
  file,
  setFile,
  parcelHandlers,
  senderHandlers,
  recipientHandlers,
  handleEditingChange,
  hasErrorsInFields,
  getAddressFields,
  getAddressFormProps,
  getAddressSummary,
  hasAddressError,
}: IDuplicateShipmentOutboundSectionProps) => (
  <Card
    size="small"
    title={`Outgoing trip${values.reference ? `: ${values.reference}` : ''}`}
  >
    <Flex vertical gap="0.75rem">
      {/* Parcel Details */}
      <EditableCard
        title="Parcel Details"
        onEditingChange={handleEditingChange}
        onEditStart={parcelHandlers.onEditStart}
        onCancel={parcelHandlers.onCancel}
        summary={
          <ParcelDetailsSummary
            carrier={values.carrier}
            weightInGrams={values.weightInGrams || ''}
            carrierTrackingCode={values.carrierTrackingCode}
            labelUrl={outboundLabelUrl}
          />
        }
      >
        <DuplicateShipmentCarrierForm
          carrier={values.carrier}
          setCarrier={(c) => setFieldValue('carrier', c)}
          file={file}
          setFile={setFile}
          weightInGrams={values.weightInGrams || ''}
          onWeightChange={(v) => setFieldValue('weightInGrams', v)}
          labelUrl={outboundLabelUrl}
          carrierOptions={carrierOptions}
        />
        {serviceTypeEnabled && serviceTypeOptions.length > 0 && (
          <Select
            data-testid="duplicate-service-type-select"
            style={{ width: '100%' }}
            placeholder="Select service type"
            value={values.serviceType || undefined}
            onChange={(v) => setFieldValue('serviceType', v)}
            options={[
              { label: 'None', value: '' },
              ...serviceTypeOptions.map((st) => ({
                label: st.name,
                value: st.value,
              })),
            ]}
          />
        )}
        <TextInput
          label="Outgoing name of trip:"
          name="reference"
          value={values.reference || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setFieldValue('reference', e.target.value)
          }
        />
        <TextInput
          label="Carrier tracking code"
          name="carrierTrackingCode"
          value={values.carrierTrackingCode || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setFieldValue('carrierTrackingCode', e.target.value)
          }
        />
      </EditableCard>

      {/* Shipping Addresses */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} data-testid="address-section-sender">
          <EditableCard
            title="Sender"
            defaultEditing={hasAddressError('sender')}
            disableDone={hasErrorsInFields(getAddressFields('sender'))}
            onEditingChange={handleEditingChange}
            onEditStart={senderHandlers.onEditStart}
            onCancel={senderHandlers.onCancel}
            summary={
              <AddressSummaryCard
                label="Sender"
                icon={<UserOutlined />}
                address={getAddressSummary('sender')}
                email={values.senderEmail}
              />
            }
          >
            <DuplicateShipmentAddressForm {...getAddressFormProps('sender')} />
          </EditableCard>
        </Col>
        <Col xs={24} md={12} data-testid="address-section-recipient">
          <EditableCard
            title="Recipient"
            defaultEditing={hasAddressError('recipient')}
            disableDone={hasErrorsInFields(getAddressFields('recipient'))}
            onEditingChange={handleEditingChange}
            onEditStart={recipientHandlers.onEditStart}
            onCancel={recipientHandlers.onCancel}
            summary={
              <AddressSummaryCard
                label="Recipient"
                icon={<HomeOutlined />}
                address={getAddressSummary('recipient')}
                email={values.recipientEmail}
              />
            }
          >
            <DuplicateShipmentAddressForm
              {...getAddressFormProps('recipient')}
            />
          </EditableCard>
        </Col>
      </Row>
    </Flex>
  </Card>
);
